import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Play, Loader2, ChevronDown, ChevronRight, AlertTriangle, AlertCircle, Info,
} from "lucide-react";
import AuditTrailTab from "@/components/AuditTrailTab";

const SEVERITY_STYLES = {
  red: "bg-red-50 text-[#C41E1E] border-red-200",
  yellow: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-green-50 text-green-700 border-green-200",
};

const SEVERITY_ICON = { red: AlertCircle, yellow: AlertTriangle, info: Info };

const PRIORITY_COLUMNS = [
  "employee_id", "name", "email", "skill_code", "skill_name", "type", "status", "result",
  "achieved_level", "required_level", "score_percent", "completed_date", "created_at",
  "training_id", "calendar_id", "date_conducted", "unknown_roles",
];

function pickColumns(records) {
  if (!records?.length) return [];
  const keys = new Set();
  records.slice(0, 30).forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
  return PRIORITY_COLUMNS.filter((c) => keys.has(c)).slice(0, 6);
}

function cellValue(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
}

function CheckRow({ check }) {
  const [open, setOpen] = useState(false);
  const cols = pickColumns(check.records);
  const Icon = SEVERITY_ICON[check.severity] || Info;
  return (
    <div className="bc-card overflow-hidden" data-testid={`audit-check-${check.check.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[#F9F9F9] transition-colors"
        data-testid="audit-check-toggle"
      >
        {check.count > 0 ? (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span className="w-4" />}
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide border rounded-sm shrink-0 ${SEVERITY_STYLES[check.severity]}`}>
          <Icon size={11} /> {check.severity}
        </span>
        <span className="text-sm font-medium flex-1">{check.check}</span>
        <span className="font-heading text-lg font-bold tabular-nums" data-testid="audit-check-count">{check.count}</span>
      </button>
      {open && check.count > 0 && (
        <div className="border-t border-[#E5E5E5] overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F9F9F9] border-b border-[#E5E5E5] sticky top-0">
              <tr>
                {cols.map((c) => <th key={c} className="text-left px-3 py-2 bc-label">{c.replace(/_/g, " ")}</th>)}
              </tr>
            </thead>
            <tbody>
              {check.records.map((r, i) => (
                <tr key={r.id ?? r.record_id ?? r.assessment_id ?? r.employee_id ?? i} className="border-b border-[#E5E5E5]">
                  {cols.map((c) => <td key={c} className="px-3 py-1.5 font-mono">{cellValue(r[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChecksTab() {
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await api.get("/admin/data-audit/run");
      setData(res.data);
      toast.success("Audit complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#666666] max-w-2xl">
          Runs a live scan across employees, assessments, and training records to surface data-quality
          issues — test accounts, orphaned records, scoring inconsistencies, and missing effectiveness checks.
        </p>
        <button
          type="button"
          onClick={runAudit}
          disabled={running}
          data-testid="run-audit-button"
          className="inline-flex items-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm px-4 py-2.5 font-medium disabled:opacity-50 shrink-0"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? "Running…" : "Run Audit"}
        </button>
      </div>

      {data && (
        <div className="flex items-center gap-4 text-sm" data-testid="audit-totals">
          <span className="text-[#666666]">Last run: {new Date(data.run_at).toLocaleString()}</span>
          <span className={`px-2 py-0.5 rounded-sm border ${SEVERITY_STYLES.red}`}>{data.totals.red} red</span>
          <span className={`px-2 py-0.5 rounded-sm border ${SEVERITY_STYLES.yellow}`}>{data.totals.yellow} yellow</span>
          <span className={`px-2 py-0.5 rounded-sm border ${SEVERITY_STYLES.info}`}>{data.totals.info} info</span>
        </div>
      )}

      {data ? (
        <div className="space-y-2">
          {data.checks.map((c) => <CheckRow key={c.check} check={c} />)}
        </div>
      ) : (
        !running && (
          <div className="bc-card px-5 py-10 text-center text-sm text-[#666666]" data-testid="audit-empty-state">
            Click "Run Audit" to scan the database for data-quality issues.
          </div>
        )
      )}
    </div>
  );
}

export default function DataAudit() {
  const [tab, setTab] = useState("checks");
  return (
    <div data-testid="data-audit-page">
      <PageHeader
        eyebrow="Super Admin · Data integrity"
        title="Data Audit"
        subtitle="Run on-demand data-quality checks and review the full create/update/delete audit trail."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Data Audit" }]}
      />
      <div className="px-4 md:px-10 py-8 space-y-6">
        <div className="flex gap-1 border-b border-[#E5E5E5]">
          <button
            type="button"
            onClick={() => setTab("checks")}
            data-testid="tab-checks"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "checks" ? "border-[#C41E1E] text-[#C41E1E]" : "border-transparent text-[#666666] hover:text-[#0D0D0D]"}`}
          >
            Data Quality Checks
          </button>
          <button
            type="button"
            onClick={() => setTab("trail")}
            data-testid="tab-trail"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "trail" ? "border-[#C41E1E] text-[#C41E1E]" : "border-transparent text-[#666666] hover:text-[#0D0D0D]"}`}
          >
            Audit Trail
          </button>
        </div>
        {tab === "checks" ? <ChecksTab /> : <AuditTrailTab />}
      </div>
    </div>
  );
}
